import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import useAddUsersToTeam from '../../../../../hooks/api/teams/useAddUsersToTeam';
import Button from '../../../../generic/Button/Button';
import ButtonGroup from '../../../../generic/Button/ButtonGroup';
import LoaderButton from '../../../../generic/Button/LoaderButton';
import FormGroup from '../../../../generic/Forms/FormGroup';
import FormLabel from '../../../../generic/Forms/FormLabel';
import LabeledSelect from '../../../../generic/Forms/LabeledSelect';
import useSelectRoles from '../hooks/useSelectRoles';
import { AddableMembersSelect } from '../shared/AddableMembersSelect';
import type { SelectOption } from '../../../../../types';

import type { AddableUser } from './AddUserOption';

const BUILT_IN_TEAM_VIEWER_ROLE_ID = 6;

type Props = {
  teamId: number;
  teamName: string;
  onHide: () => void;
};

/**
 * The modal for adding users to a team. It will fetch users that are not already in the team (using the useAddableMembers query)
 * and the roles (using the new `useSelectRoles` hook). The user will then be able to select the users they want to add
 * and the default role they want to set for them on the team.
 *
 * @param {Props} props - The props for the component.
 * @param {number} props.teamId - The ID of the team to add users to.
 * @param {string} props.teamName - The name of the team to add users to.
 * @param {() => void} props.onHide - The callback to hide the modal.
 * @returns {VoidFunctionComponent} The component.
 */
const AddMembersModal: VoidFunctionComponent<Props> = ({ teamId, teamName, onHide }) => {
  //  State:
  const [selectedRole, setSelectedRole] = useState<SelectOption<number> | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<AddableUser[]>([]);
  //  Data fetching
  const { roleOptions, isLoading: isLoadingRoles } = useSelectRoles({
    selectedRoleId: BUILT_IN_TEAM_VIEWER_ROLE_ID,
    teamId,
  });

  const isLoadingAny = isLoadingRoles;

  const { mutateAsync: addUsersToTeam, isLoading: isAddingUsersToTeam } = useAddUsersToTeam();

  const handleSave = async () => {
    await addUsersToTeam({
      teamId,
      users: selectedUsers.map((user) => ({ id: user.value, roleId: selectedRole?.value })),
    });
    onHide();
    //  Reset the state:
    setSelectedRole(null);
    setSelectedUsers([]);
  };

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title className="margin-bottom-sm">Add Members to Team &quot;{teamName}&quot;</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormGroup>
          <FormLabel label="Select Users">
            <AddableMembersSelect
              id="add-members-modal-user-select"
              teamId={teamId}
              value={selectedUsers}
              onChange={setSelectedUsers}
            />
          </FormLabel>
          <LabeledSelect
            label="Team Role"
            id="defaultRoleId"
            options={roleOptions}
            onChange={setSelectedRole}
            value={selectedRole}
            isDisabled={isLoadingRoles}
            placeholder="Select the role for these users on the team"
          />
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button type="secondary" size="medium" onClick={onHide} isDisabled={isLoadingAny}>
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            size="medium"
            onClick={handleSave}
            isDisabled={isLoadingAny || selectedUsers.length === 0 || !selectedRole}
            isLoading={isAddingUsersToTeam}
          >
            {selectedUsers.length > 0 ? `Add ${selectedUsers.length} Users` : 'Add Users'}
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default AddMembersModal;
